/**
 * AIImagePromptOptions
 *
 * Shared, provider-aware controls for SD image generation:
 *   - "Use suggested quality prompts" toggle
 *   - collapsible "Add negative prompt" textarea
 *
 * Reused by BOTH the image tab (PromptInput) and the video tab
 * (MediaSourceSelector's Fooocus / ComfyUI-SD panels) so the two surfaces stay
 * in sync (object-orientation: one component, two call sites). The caller owns
 * the semantics of the toggle:
 *   - Image tab: toggling mutates the user's typed prompt (adds boosters inline).
 *   - Video tab: toggling sets a persistent flag the backend applies per scene.
 *
 * This component is purely presentational + local UI state (expand/collapse).
 */

import { useEffect, useState, memo } from 'react';
import { ChevronDown, ChevronUp, Sparkles, Info } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { SUGGESTED_POSITIVE_PROMPT, SUGGESTED_NEGATIVE_PROMPT } from '../../constants/imagePrompts';

interface AIImagePromptOptionsProps {
  /** Current negative prompt text. */
  negativePrompt: string;
  onNegativePromptChange: (value: string) => void;
  /** Whether the "suggested quality prompts" control is enabled. */
  useSuggestedQuality: boolean;
  onUseSuggestedQualityChange: (checked: boolean) => void;
  disabled?: boolean;
  /**
   * Optional helper shown under the negative prompt textarea. Lets the video
   * tab clarify that suggested negatives are auto-applied server-side.
   */
  negativeHint?: string;
}

export const AIImagePromptOptions = memo(function AIImagePromptOptions({
  negativePrompt,
  onNegativePromptChange,
  useSuggestedQuality,
  onUseSuggestedQualityChange,
  disabled = false,
  negativeHint,
}: AIImagePromptOptionsProps) {
  const { t } = useTranslation();
  const [showNegative, setShowNegative] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Reveal the negative section automatically when suggested quality is enabled
  // (mirrors the legacy PromptInput behavior, and helps the user see the field).
  useEffect(() => {
    if (useSuggestedQuality) setShowNegative(true);
  }, [useSuggestedQuality]);

  return (
    <div className="space-y-4">
      {/* Suggested quality prompts */}
      <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2">
        <label className="flex items-start gap-2.5 cursor-pointer min-h-[44px] sm:min-h-0">
          <input
            type="checkbox"
            checked={useSuggestedQuality}
            onChange={(e) => onUseSuggestedQualityChange(e.target.checked)}
            disabled={disabled}
            className="mt-0.5 h-4 w-4 flex-shrink-0 accent-primary-500"
            data-testid="image-use-suggested-prompts"
          />
          <span className="flex-1">
            <span className="flex items-center gap-1.5 text-sm font-medium text-white">
              <Sparkles className="h-3.5 w-3.5 text-amber-300" />
              {t('image.prompt.suggested.label', {
                defaultValue: 'Use suggested quality prompts',
              })}
            </span>
            <span className="block text-xs text-white/50 mt-0.5">
              {t('image.prompt.suggested.subtitle', {
                defaultValue:
                  'Recommended keywords that improve sharpness and detail on SD models.',
              })}
            </span>
          </span>
        </label>

        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors"
        >
          <Info className="h-3 w-3" />
          {t('image.prompt.suggested.whatIsThis', {
            defaultValue: 'What are positive and negative prompts?',
          })}
        </button>

        {showHelp && (
          <div className="text-xs text-white/60 space-y-1.5 pl-1 border-l-2 border-white/10 ml-1">
            <p className="pl-2">
              <span className="font-semibold text-white/80">
                {t('image.prompt.suggested.positiveTitle', { defaultValue: 'Positive prompt' })}:
              </span>{' '}
              {t('image.prompt.suggested.positiveHelp', {
                defaultValue: 'what you WANT in the image (subject, style, lighting, quality).',
              })}
            </p>
            <p className="pl-2">
              <span className="font-semibold text-white/80">
                {t('image.prompt.suggested.negativeTitle', { defaultValue: 'Negative prompt' })}:
              </span>{' '}
              {t('image.prompt.suggested.negativeHelp', {
                defaultValue:
                  'what you want to AVOID (blur, extra fingers, watermarks, low quality).',
              })}
            </p>
            <div className="pl-2 pt-1 space-y-1">
              <p className="text-white/40">
                <span className="text-amber-300/80">+ </span>
                <code className="text-[11px] break-words">{SUGGESTED_POSITIVE_PROMPT}</code>
              </p>
              <p className="text-white/40">
                <span className="text-red-300/80">− </span>
                <code className="text-[11px] break-words">{SUGGESTED_NEGATIVE_PROMPT}</code>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Negative Prompt Section */}
      <button
        type="button"
        onClick={() => setShowNegative(!showNegative)}
        className="flex items-center gap-2 text-sm text-secondary-600 hover:text-secondary-800 dark:text-secondary-400 dark:hover:text-secondary-200 transition-colors"
        disabled={disabled}
      >
        {showNegative ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        <span>
          {showNegative
            ? t('image.prompt.negative.hide', { defaultValue: 'Hide negative prompt' })
            : t('image.prompt.negative.toggle', { defaultValue: 'Add negative prompt' })}
        </span>
      </button>

      {showNegative && (
        <div className="form-group animate-in slide-in-from-top-2 duration-200">
          <label htmlFor="negative-prompt" className="form-label">
            {t('image.prompt.negative.label', { defaultValue: 'Negative Prompt' })}
          </label>
          <textarea
            id="negative-prompt"
            value={negativePrompt}
            onChange={(e) => onNegativePromptChange(e.target.value)}
            placeholder={t('image.prompt.negative.placeholder', {
              defaultValue: 'What to avoid in the image...',
            })}
            className="input min-h-[80px] resize-y"
            disabled={disabled}
            data-testid="image-negative-prompt-input"
          />
          <p className="text-xs text-secondary-500 mt-1">
            {negativeHint ??
              t('image.prompt.negative.hint', {
                defaultValue: 'Describe elements you want to exclude from the generated image',
              })}
          </p>
        </div>
      )}
    </div>
  );
});

export default AIImagePromptOptions;
